/**
 * Single entry point to Grafana Faro (RUM). Components and stores call these
 * functions only; nothing else imports @grafana/faro-*. Every function is a
 * no-op when Faro is disabled (VITE_FARO_ENABLED !== 'true').
 */
import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import {
  buildFaroUser,
  type FaroEnv,
  type FaroUser,
  type PageContext,
  readFaroConfig,
  resolveUserActionName
} from './faroContext'

/** The slice of the Faro API the tracker uses (lets tests pass a fake). */
export type TrackerApi = {
  pushEvent(name: string, attributes?: Record<string, string>): void
  setUser(user: FaroUser): void
  resetUser(): void
  resetSession?(): void
  setView(view: { name: string }): void
  startUserAction?(name: string): unknown
  pushError?(error: Error, options?: { context?: Record<string, string> }): void
  getSession(): { id?: string } | undefined
}

type TrackerStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

type ObservedUser = Parameters<typeof buildFaroUser>[0]

const OBSERVED_USER_KEY = 'faro.observedUser'

const asString = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

/**
 * @param api Faro API (null when Faro is disabled)
 * @param storage sessionStorage-like store keeping the last observed user, so
 *   the first page view of a full reload is attributed before loadUser resolves.
 */
export function createTracker(
  api: TrackerApi | null,
  storage: TrackerStorage | null = null
) {
  const push = (name: string, attributes: Record<string, unknown>) => {
    if (!api) {
      return
    }
    const stringified = Object.fromEntries(
      Object.entries(attributes).map(([key, value]) => [key, asString(value)])
    )
    api.pushEvent(name, stringified)
  }

  return {
    setObservedUser(user: ObservedUser, tenant: string | null | undefined) {
      const faroUser = buildFaroUser(user, tenant)
      if (!api || !faroUser) {
        return
      }
      api.setUser(faroUser)
      storage?.setItem(OBSERVED_USER_KEY, JSON.stringify(faroUser))
    },
    // isAuthenticated: an expired token or a 401 redirects to /connect through
    // a full reload; the previous user must not be restored on that page.
    restoreObservedUser(isAuthenticated: () => boolean) {
      if (!api || !storage || !isAuthenticated()) {
        return
      }
      try {
        const faroUser = JSON.parse(
          storage.getItem(OBSERVED_USER_KEY) || 'null'
        )
        if (faroUser?.id) {
          api.setUser(faroUser)
        }
      } catch {
        storage.removeItem(OBSERVED_USER_KEY)
      }
    },
    clearObservedUser() {
      api?.resetUser()
      // A new session too: two accounts in the same tab must never share one.
      api?.resetSession?.()
      storage?.removeItem(OBSERVED_USER_KEY)
    },
    trackPageView(page: PageContext) {
      if (!api) {
        return
      }
      api.setView({ name: page.route })
      push('page_view', page)
    },
    trackRouteLoaded(page: PageContext, durationMs: number) {
      push('route_loaded', { ...page, duration_ms: Math.round(durationMs) })
    },
    trackModalOpen(modal: string, route: string) {
      push('modal_open', { modal, route })
    },
    trackModalClose(modal: string, route: string, durationMs: number) {
      push('modal_close', { modal, route, duration_ms: Math.round(durationMs) })
    },
    trackButtonClick(button: string, route: string) {
      push('button_click', { button, route })
    },
    startUserAction(name: string) {
      api?.startUserAction?.(name)
    },
    /** Errors the app recovers from silently, so Loki still shows them (e.g. a PDF that will not load). */
    trackError(error: unknown, context: Record<string, unknown> = {}) {
      const normalized =
        error instanceof Error ? error : new Error(asString(error))
      const stringified = Object.fromEntries(
        Object.entries(context).map(([key, value]) => [key, asString(value)])
      )
      api?.pushError?.(normalized, { context: stringified })
    },
    getFaroSessionId(): string | null {
      return api?.getSession()?.id ?? null
    }
  }
}

let tracker = createTracker(null)
let enabled = false

/** True once initObservability started Faro: lets callers skip costly work. */
export const isFaroEnabled = (): boolean => enabled

export function initObservability({ env }: { env: FaroEnv }): void {
  const config = readFaroConfig(env, window.location.hostname)
  if (!config.enabled) {
    return
  }
  const faro = initializeFaro({
    url: config.url,
    app: {
      name: 'funeral-manager-front',
      version: config.version,
      environment: config.environment
    },
    // 100% of sessions: B2B volume is low and usage stats must be exact (spec §4.1).
    sessionTracking: { samplingRate: 1 },
    instrumentations: [
      // Includes web vitals, errors, sessions, views and user actions
      // (data-faro-user-action-name) in Faro 2.11.
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        // Browser spans carry the same resource.country as the API spans, so
        // the per-country dashboards (TraceQL `resource.country`) see them.
        resourceAttributes: config.country ? { country: config.country } : {},
        instrumentationOptions: {
          // Must be nested under instrumentationOptions (a top-level option is
          // silently ignored and no traceparent is sent).
          propagateTraceHeaderCorsUrls: [/\/api\//]
        }
      })
    ]
  })
  tracker = createTracker(faro.api, window.sessionStorage)
  enabled = true
  tracker.restoreObservedUser(() =>
    Boolean(window.localStorage.getItem('token'))
  )

  // Nested click targets inside a tagged button (see resolveUserActionName).
  window.addEventListener(
    'pointerdown',
    (event) => {
      const actionName = resolveUserActionName(event.target)
      if (actionName) {
        tracker.startUserAction(actionName)
      }
    },
    { capture: true }
  )
}

export const setObservedUser = (
  user: ObservedUser,
  tenant: string | null | undefined
) => tracker.setObservedUser(user, tenant)
export const clearObservedUser = () => tracker.clearObservedUser()
export const trackPageView = (page: PageContext) => tracker.trackPageView(page)
export const trackRouteLoaded = (page: PageContext, durationMs: number) =>
  tracker.trackRouteLoaded(page, durationMs)
export const trackModalOpen = (modal: string, route: string) =>
  tracker.trackModalOpen(modal, route)
export const trackModalClose = (
  modal: string,
  route: string,
  durationMs: number
) => tracker.trackModalClose(modal, route, durationMs)
export const trackButtonClick = (button: string, route: string) =>
  tracker.trackButtonClick(button, route)
export const startUserAction = (name: string) => tracker.startUserAction(name)
export const trackError = (error: unknown, context?: Record<string, unknown>) =>
  tracker.trackError(error, context)
export const getFaroSessionId = () => tracker.getFaroSessionId()
